import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { adminAPI } from '../api';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, FileText, Calendar, TrendingUp, Activity,
  RefreshCw, Lock, CheckCircle2, XCircle, Flag, Shield, Eye,
  Trash2, Ban, Undo2
} from 'lucide-react';

const TABS = ['metrics', 'moderation'];

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('metrics');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Moderation state
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportFilter, setReportFilter] = useState('pending');
  const [reasonFilter, setReasonFilter] = useState('');
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [revealBody, setRevealBody] = useState(false);
  const [fullBody, setFullBody] = useState(null);
  // Go-Live state
  const [goLiveStatus, setGoLiveStatus] = useState(null);
  const [liveChecks, setLiveChecks] = useState(null);
  const [runningChecks, setRunningChecks] = useState(false);
  // Slow queries state
  const [slowQueries, setSlowQueries] = useState([]);
  const [loadingSlowQueries, setLoadingSlowQueries] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setAccessDenied(false);
      const metricsData = await adminAPI.getMetrics();
      setMetrics(metricsData);
      try { setSchedulerStatus(await adminAPI.getSchedulerStatus()); } catch {}
      try { setGoLiveStatus(await adminAPI.getGoLiveStatus()); } catch {}
      try { const sq = await adminAPI.getSlowQueries(50); setSlowQueries(sq.queries || []); } catch {}
    } catch (error) {
      if (error.response?.status === 403) { setAccessDenied(true); return; }
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  const handleRunLiveChecks = async () => {
    setRunningChecks(true);
    try {
      const data = await adminAPI.runLiveChecks();
      setLiveChecks(data.checks);
      toast.success('Live checks completed');
    } catch { toast.error('Failed to run checks'); }
    finally { setRunningChecks(false); }
  };

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const params = {};
      if (reportFilter) params.status = reportFilter;
      if (reasonFilter) params.reason = reasonFilter;
      const data = await adminAPI.getReports(params);
      setReports(data.reports);
      setReportsTotal(data.total);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally { setLoadingReports(false); }
  };

  useEffect(() => { if (tab === 'moderation') loadReports(); }, [tab, reportFilter, reasonFilter]);

  const openReport = async (reportId) => {
    try {
      const detail = await adminAPI.getReportDetail(reportId);
      setSelectedReport(detail);
      setRevealBody(false);
      setFullBody(null);
      setResolveNote('');
      setRemoveReason('');
      setSuspendReason('');
    } catch { toast.error('Failed to load report'); }
  };

  const handleRevealBody = async () => {
    try {
      const data = await adminAPI.getComment(selectedReport.comment_id, true);
      setFullBody(data.body);
      setRevealBody(true);
    } catch { toast.error('Failed to load comment body'); }
  };

  const handleRemoveComment = async () => {
    if (!removeReason.trim()) { toast.error('Reason required'); return; }
    try {
      await adminAPI.removeComment({ comment_id: selectedReport.comment_id, reason: removeReason });
      toast.success('Comment removed');
      loadReports();
      openReport(selectedReport.report_id);
    } catch { toast.error('Failed to remove comment'); }
  };

  const handleSuspendUser = async () => {
    if (!suspendReason.trim()) { toast.error('Reason required'); return; }
    try {
      await adminAPI.suspendUser({ user_id: selectedReport.reported_user_id, reason: suspendReason });
      toast.success('User suspended');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to suspend'); }
  };

  const handleResolve = async () => {
    if (!resolveNote.trim()) { toast.error('Resolution note required'); return; }
    try {
      await adminAPI.resolveReport(selectedReport.report_id, { resolution_note: resolveNote });
      toast.success('Report resolved');
      setSelectedReport(null);
      loadReports();
    } catch { toast.error('Failed to resolve report'); }
  };

  const handleRefresh = async () => { setRefreshing(true); await loadData(); setRefreshing(false); toast.success('Refreshed'); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
    </div>
  );

  if (accessDenied) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader><Lock className="h-8 w-8 text-red-600 mb-2" /><CardTitle>Access Denied</CardTitle></CardHeader>
        <CardContent><Button onClick={() => navigate('/home')} className="w-full">Go to Dashboard</Button></CardContent>
      </Card>
    </div>
  );

  const categoryLabel = (c) => ({ phi: 'PHI', spam: 'Spam', harassment: 'Harassment', misinformation: 'Misinfo', other: 'Other' }[c] || c);
  const categoryColor = (c) => ({ phi: 'bg-red-100 text-red-700', spam: 'bg-yellow-100 text-yellow-700', harassment: 'bg-orange-100 text-orange-700', misinformation: 'bg-purple-100 text-purple-700' }[c] || 'bg-gray-100 text-gray-700');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/home')} className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-2" />Back
              </Button>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            </div>
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <Button variant={tab === 'metrics' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('metrics')} data-testid="tab-metrics">
              <Activity className="h-4 w-4 mr-1.5" />Metrics
            </Button>
            <Button variant={tab === 'moderation' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('moderation')} data-testid="tab-moderation">
              <Shield className="h-4 w-4 mr-1.5" />Moderation
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/copilot-dashboard')} data-testid="tab-ai-dashboard">
              <TrendingUp className="h-4 w-4 mr-1.5" />AI Usage
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === 'metrics' && metrics && (
          <>
            {schedulerStatus && (
              <Card className="mb-6"><CardHeader><CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5 text-purple-600" />Scheduler</CardTitle></CardHeader>
                <CardContent><div className="flex items-center gap-2">{schedulerStatus.is_running ? <><CheckCircle2 className="h-5 w-5 text-green-600" /><span className="font-semibold text-green-600">Running</span></> : <><XCircle className="h-5 w-5 text-red-600" /><span className="font-semibold text-red-600">Stopped</span></>}</div></CardContent>
              </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card><CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" />Users</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{metrics.users?.total || 0}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" />Verified</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{metrics.users?.verified || 0}</div><div className="text-sm text-gray-600">{metrics.users?.verification_rate}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" />Articles</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{metrics.articles?.total_indexed || 0}</div></CardContent></Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card><CardHeader><CardTitle className="text-lg"><Calendar className="h-5 w-5 text-blue-600 inline mr-2" />Digests 24h</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{metrics.digests_24h?.total || 0}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg"><CheckCircle2 className="h-5 w-5 text-green-600 inline mr-2" />Sent</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-green-600">{metrics.digests_24h?.sent || 0}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg"><TrendingUp className="h-5 w-5 text-blue-600 inline mr-2" />Useful</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{metrics.feedback?.useful || 0}</div></CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg"><XCircle className="h-5 w-5 text-red-600 inline mr-2" />Not Relevant</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-red-600">{metrics.feedback?.not_relevant || 0}</div></CardContent></Card>
            </div>

            {/* Moderation Summary Widget (Step 4) */}
            {metrics.moderation && (
              <Card className="mt-6" data-testid="moderation-summary-widget">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Flag className="h-5 w-5 text-red-500" />Moderation Summary</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                    <div>
                      <div className="text-sm text-gray-500">Pending Reports</div>
                      <div className="text-2xl font-bold text-gray-900">{metrics.moderation.pending_reports_total}</div>
                      <div className="text-xs text-red-600">{metrics.moderation.pending_reports_phi} PHI</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Avg Resolution Time</div>
                      <div className="text-2xl font-bold text-gray-900">{metrics.moderation.avg_resolution_time_hours_last_30d != null ? `${metrics.moderation.avg_resolution_time_hours_last_30d}h` : '—'}</div>
                      <div className="text-xs text-gray-500">Last 30 days</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">PHI Reports (14d)</div>
                      <div className="flex items-end gap-0.5 h-8 mt-1">
                        {(metrics.moderation.phi_reports_timeseries_last_14d || []).map((d, i) => (
                          <div key={i} className="flex-1 bg-red-200 rounded-t" style={{height: `${Math.max(d.count * 20, 2)}px`}} title={`${d.date}: ${d.count}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Audio Ops Widget (Step 9) */}
            {metrics.audio && (
              <Card className="mt-6" data-testid="audio-ops-widget">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2">Audio Operations</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div><div className="text-sm text-gray-500">Ready</div><div className="text-2xl font-bold text-emerald-600">{metrics.audio.ready_count}</div></div>
                    <div><div className="text-sm text-gray-500">Pending</div><div className="text-2xl font-bold text-amber-500">{metrics.audio.pending_count}</div></div>
                    <div><div className="text-sm text-gray-500">Failed</div><div className="text-2xl font-bold text-red-500">{metrics.audio.failed_count}</div></div>
                    <div><div className="text-sm text-gray-500">Generated 24h</div><div className="text-2xl font-bold text-gray-900">{metrics.audio.generated_last_24h}</div></div>
                    <div><div className="text-sm text-gray-500">Failures 24h</div><div className="text-2xl font-bold text-gray-900">{metrics.audio.failures_last_24h}</div></div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Go-Live Readiness Panel (Step 11) */}
            {goLiveStatus && (
              <Card className="mt-6" data-testid="go-live-panel">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2"><Shield className="h-5 w-5 text-blue-600" />Go-Live Readiness</CardTitle>
                    <Button variant="outline" size="sm" onClick={handleRunLiveChecks} disabled={runningChecks} data-testid="run-live-checks-btn">
                      {runningChecks ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Running...</> : <><RefreshCw className="h-4 w-4 mr-1" />Run Live Checks</>}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Environment */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Environment</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${goLiveStatus.environment?.config_validation_ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span>{goLiveStatus.environment?.name || 'unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${goLiveStatus.environment?.jwt_secret_set ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span>JWT Key</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${!goLiveStatus.environment?.cors_is_wildcard ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <span>CORS</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${goLiveStatus.environment?.app_base_url_set ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span>Base URL</span>
                      </div>
                    </div>
                  </div>

                  {/* Integrations */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Integrations</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { label: 'SendGrid', ok: goLiveStatus.integrations?.sendgrid?.configured },
                        { label: 'Stripe Billing', ok: goLiveStatus.integrations?.stripe?.billing_enabled && goLiveStatus.integrations?.stripe?.secret_key_configured },
                        { label: `Audio TTS (${goLiveStatus.integrations?.audio?.tts_provider || 'off'})`, ok: goLiveStatus.integrations?.audio?.enabled_flag && goLiveStatus.integrations?.audio?.openai_key_configured },
                        { label: `Storage (${goLiveStatus.integrations?.audio?.storage_backend || 'local'})`, ok: goLiveStatus.integrations?.audio?.storage_backend === 's3' ? goLiveStatus.integrations?.audio?.s3_configured : true },
                        { label: `Lit Scholar (${goLiveStatus.integrations?.copilot?.provider || 'off'})`, ok: goLiveStatus.integrations?.copilot?.enabled_flag && goLiveStatus.integrations?.copilot?.provider_key_configured, testid: 'litscholar-status' },
                        { label: 'Scheduler', ok: goLiveStatus.operations?.scheduler?.running },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded" data-testid={item.testid}>
                          <span className={`w-2.5 h-2.5 rounded-full ${item.ok ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                          <span className={item.ok ? 'text-gray-900' : 'text-gray-500'}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Live Check Results */}
                  {liveChecks && (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">Live Check Results</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(liveChecks).map(([key, val]) => (
                          <div key={key} className={`text-sm px-3 py-2 rounded border ${val.status === 'ok' ? 'bg-emerald-50 border-emerald-200' : val.status === 'skipped' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`} data-testid={`check-${key}`}>
                            <span className="font-medium">{key}</span>: <span className={val.status === 'ok' ? 'text-emerald-700' : val.status === 'skipped' ? 'text-gray-500' : 'text-red-700'}>{val.status}</span>
                            {val.message && <span className="text-xs text-gray-500 block mt-0.5">{val.message}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View Deployment Playbook</a>
                </CardContent>
              </Card>
            )}

            {/* Slow Queries Panel (Step 17) */}
            <Card className="mt-6" data-testid="slow-queries-panel">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-amber-600" />Slow Queries (last {slowQueries.length})
                </CardTitle>
                <CardDescription>DB operations exceeding 200ms threshold. No query bodies stored (PHI-Zero).</CardDescription>
              </CardHeader>
              <CardContent>
                {slowQueries.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center" data-testid="slow-queries-empty">No slow queries recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="slow-queries-table">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="py-2 pr-4 font-medium">Time</th>
                          <th className="py-2 pr-4 font-medium">Duration</th>
                          <th className="py-2 pr-4 font-medium">Collection</th>
                          <th className="py-2 pr-4 font-medium">Op</th>
                          <th className="py-2 font-medium">Route</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slowQueries.map((q, i) => (
                          <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-1.5 pr-4 text-xs text-gray-500 whitespace-nowrap">{q.timestamp ? new Date(q.timestamp).toLocaleTimeString() : '—'}</td>
                            <td className="py-1.5 pr-4 whitespace-nowrap">
                              <span className={`font-mono font-medium ${q.duration_ms >= 1000 ? 'text-red-600' : q.duration_ms >= 500 ? 'text-amber-600' : 'text-gray-700'}`}>
                                {q.duration_ms?.toFixed(0)}ms
                              </span>
                            </td>
                            <td className="py-1.5 pr-4 text-gray-700">{q.collection}</td>
                            <td className="py-1.5 pr-4"><Badge variant="outline" className="text-xs">{q.operation}</Badge></td>
                            <td className="py-1.5 text-xs text-gray-500 truncate max-w-[200px]">{q.route || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {tab === 'moderation' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <select value={reportFilter} onChange={(e) => setReportFilter(e.target.value)} className="rounded-md border px-3 py-1.5 text-sm" data-testid="report-status-filter">
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="all">All</option>
              </select>
              <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} className="rounded-md border px-3 py-1.5 text-sm" data-testid="report-reason-filter">
                <option value="">All reasons</option>
                <option value="phi">PHI</option>
                <option value="spam">Spam</option>
                <option value="harassment">Harassment</option>
                <option value="misinformation">Misinformation</option>
                <option value="other">Other</option>
              </select>
              <span className="text-sm text-gray-500">{reportsTotal} report(s)</span>
            </div>

            {/* Report list */}
            {loadingReports ? (
              <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" /></div>
            ) : reports.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Flag className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No reports found</p></CardContent></Card>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => (
                  <Card key={r.report_id} className="cursor-pointer hover:border-blue-200 transition-colors" onClick={() => openReport(r.report_id)} data-testid={`report-row-${r.report_id}`}>
                    <CardContent className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={`text-xs ${categoryColor(r.reason_category)}`}>{categoryLabel(r.reason_category)}</Badge>
                        <span className="text-sm text-gray-700 truncate max-w-xs">{r.reason || 'No details'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={r.status === 'pending' ? 'destructive' : 'secondary'} className="text-xs">{r.status}</Badge>
                        <span className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Report detail modal */}
            {selectedReport && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setSelectedReport(null); }}>
                <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" data-testid="report-detail-modal">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Flag className="h-5 w-5 text-red-500" />Report Detail
                    </CardTitle>
                    <CardDescription>Report ID: {selectedReport.report_id?.slice(0, 8)}...</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Context */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Thread:</span> <span className="font-medium">{selectedReport.thread_title || 'Unknown'}</span></div>
                      <div><span className="text-gray-500">Specialty:</span> <span className="font-medium">{selectedReport.specialty_id || '—'}</span></div>
                      <div><span className="text-gray-500">Author:</span> <span className="font-medium">{selectedReport.comment_author_name || 'Unknown'}</span></div>
                      <div><span className="text-gray-500">Category:</span> <Badge className={`text-xs ${categoryColor(selectedReport.reason_category)}`}>{categoryLabel(selectedReport.reason_category)}</Badge></div>
                      <div><span className="text-gray-500">Status:</span> <Badge variant={selectedReport.status === 'pending' ? 'destructive' : 'secondary'}>{selectedReport.status}</Badge></div>
                      <div><span className="text-gray-500">Reported:</span> {new Date(selectedReport.created_at).toLocaleString()}</div>
                    </div>
                    {selectedReport.reason && <div className="bg-gray-50 rounded p-3 text-sm"><span className="text-gray-500">Reporter note:</span> {selectedReport.reason}</div>}

                    {/* Comment preview */}
                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Comment preview</div>
                      <p className="text-sm text-gray-700">{revealBody && fullBody ? fullBody : selectedReport.comment_body_preview || '[No body]'}</p>
                      {!revealBody && (
                        <Button variant="ghost" size="sm" className="mt-2 text-xs text-amber-600" onClick={handleRevealBody} data-testid="reveal-body-btn">
                          <Eye className="h-3 w-3 mr-1" />Reveal full text (may contain PHI)
                        </Button>
                      )}
                    </div>

                    {/* Actions */}
                    {selectedReport.status === 'pending' && (
                      <div className="space-y-3 border-t pt-3">
                        <div className="flex items-center gap-2">
                          <Textarea placeholder="Reason for removal..." value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} className="min-h-[40px] text-sm flex-1" />
                          <Button variant="destructive" size="sm" onClick={handleRemoveComment} disabled={!removeReason.trim()} data-testid="remove-comment-btn">
                            <Trash2 className="h-4 w-4 mr-1" />Remove
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Textarea placeholder="Reason for suspension..." value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} className="min-h-[40px] text-sm flex-1" />
                          <Button variant="outline" size="sm" onClick={handleSuspendUser} disabled={!suspendReason.trim()} data-testid="suspend-user-btn">
                            <Ban className="h-4 w-4 mr-1" />Suspend
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Textarea placeholder="Resolution note..." value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} className="min-h-[40px] text-sm flex-1" data-testid="resolve-note-input" />
                          <Button size="sm" onClick={handleResolve} disabled={!resolveNote.trim()} data-testid="resolve-report-btn">
                            <CheckCircle2 className="h-4 w-4 mr-1" />Resolve
                          </Button>
                        </div>
                      </div>
                    )}
                    {selectedReport.status === 'resolved' && selectedReport.resolution_note && (
                      <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                        <span className="text-green-700 font-medium">Resolved:</span> {selectedReport.resolution_note}
                      </div>
                    )}

                    <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => setSelectedReport(null)}>Close</Button></div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
